import React, { useMemo } from 'react';
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import { darken, lighten } from '@mui/material/styles';

export function AppTheme({ children }) {
  const theme = useMemo(() => {
    const palette = {
      mode: 'light',
      primary: {
        main:  '#C4A47F',
        light: lighten('#C4A47F', 0.3),
        dark:  darken('#C4A47F', 0.3),
      },
      secondary: {
        main:  '#14213d',
        light: lighten('#14213d', 0.4),
        dark:  darken('#14213d', 0.2),
      },
      grey: {
        main: '#424242',
      },
      background: {
        default: '#FFFFFF',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#211F1C',
        secondary: '#6B6258',
      },
      // Custom palette colors
      paper2: '#F2E9DC',
      ink: '#211F1C',
      ink2: '#6B6258',
      ink3: '#9C9286',
      woodDark: '#9A7A52',
      line: 'rgba(33,31,28,.12)',

      // Shared rainbow animations for non-default parameters
      animations: {
        rainbow: {
          animation: 'rainbow 4s ease-in-out infinite',
          '@keyframes rainbow': {
            '0%, 100%': { color: '#C4A47F' },
            '16%': { color: '#ff0000' },
            '33%': { color: '#ff8800' },
            '50%': { color: '#ffd700' },
            '66%': { color: '#00ff00' },
            '83%': { color: '#0088ff' },
          }
        }
      }
    };

    const components = {
      MuiButton: {
        styleOverrides: {
          containedPrimary: ({ theme }) => ({
            color: theme.palette.common.white,
          }),
          containedSecondary: ({ theme }) => ({
            color: theme.palette.common.white,
          }),
          outlined: () => ({
            color: palette.secondary.main,
            borderColor: palette.secondary.main,
          })
        }
      },
      MuiFab: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.common.white,
          })
        }
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: '#1976d2',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            }
          }
        }
      },
      MuiToggleButton: {
        variants: [
          {
            props: { variant: 'contained' },
            style: ({ theme }) => ({
              textTransform: 'none',
              fontSize: '16px',
              color: palette.primary.main,
              borderColor: palette.primary.main,
              '&.Mui-selected, &.Mui-selected:hover': {
                color: theme.palette.common.white,
                backgroundColor: palette.primary.main,
              },
              '&.Mui-disabled': {
                color: palette.primary.main,
                backgroundColor: palette.grey.main,
              },
            }),
          },
        ],
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${palette.line}`,
            boxShadow: '0 2px 8px rgba(33,31,28,.12)',
          }
        }
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: palette.primary.main,
          }
        }
      },
      MuiCssBaseline: {
        styleOverrides: `
          body {
            --paper: #fbfaf7;
            --grid-minor: rgb(67 191 236 / 15%);
            --grid-major: rgb(35 174 226 / 32%);
            --minor-size: 18px;
            --major-size: 72px;

            background-color: var(--paper) !important;

            background-image:
              /* Center fade — radial, fades grid toward center */
              radial-gradient(ellipse at center, var(--paper) 35%, transparent 95%),

              /* Minor horizontal lines */
              linear-gradient(to bottom, var(--grid-minor) 1px, transparent 1px),

              /* Minor vertical lines */
              linear-gradient(to right, var(--grid-minor) 1px, transparent 1px),

              /* Major horizontal lines */
              linear-gradient(to bottom, var(--grid-major) 1px, transparent 1px),

              /* Major vertical lines */
              linear-gradient(to right, var(--grid-major) 1px, transparent 1px),

              /* Warm vignette overlay */
              radial-gradient(ellipse at center, rgb(255 255 255 / 85%) 0%, rgb(251 247 241 / 70%) 70%),

              /* Paper variation */
              radial-gradient(circle at 20% 30%, rgb(255 255 255 / 55%), transparent 40%),
              radial-gradient(circle at 75% 65%, rgb(210 200 185 / 8%), transparent 45%) !important;

            background-size:
              100% 100%,
              var(--minor-size) var(--minor-size),
              var(--minor-size) var(--minor-size),
              var(--major-size) var(--major-size),
              var(--major-size) var(--major-size),
              100% 100%,
              100% 100%,
              100% 100% !important;

            background-position: -1px -1px !important;
            background-attachment: fixed !important;
          }
        `
      }
    };

    return createTheme({
      palette,
      components,
      typography: {
        fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
      }
    });
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
